import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LOGO_SVG, doctorDisplay, buildWhatsAppBrandedMessage, doctorPhotoUrl } from "../lib/clinic";
import { FITNESS_LEVELS, CLINICAL_CONSTRAINTS } from "../lib/exerciseTypes";

export default function PrintExercisePlan() {
  const { planId } = useParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/exercise/plans/${planId}`);
        setPlan(r.data.plan);
      } catch (e) {
        console.warn("load exercise plan failed:", e);
        toast.error("Could not load exercise plan");
      }
    })();
  }, [planId]);

  if (!plan) return <div className="p-8 text-sm text-slate-500">Loading exercise plan…</div>;
  const p = plan.patient_snapshot || {};
  const isMr = plan.language === "mr";
  const date = new Date(plan.issued_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const fit = FITNESS_LEVELS.find((f) => f.id === plan.preferences?.fitness_level);
  const constraintList = (plan.preferences?.constraints || []).map((cid) => CLINICAL_CONSTRAINTS.find((x) => x.id === cid)).filter(Boolean);
  const constraintsStr = constraintList.length ? constraintList.map((c) => (isMr ? c.name_mr : c.name_en)).join(" · ") : (isMr ? "काहीही नाही" : "None reported");

  const shareWA = () => {
    if (!plan.plan_text) return;
    const patient = plan.patient_snapshot || {};
    const fitLabel = fit ? (isMr ? fit.name_mr : fit.name_en) : (isMr ? "व्यायाम आराखडा" : "Movement Plan");
    const msg = buildWhatsAppBrandedMessage({
      lang: isMr ? "mr" : "en",
      title: fitLabel,
      body: plan.plan_text,
      patientName: patient.name,
      kind: "exercise",
      dateStr: date,
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50 print-shell">
      <Toaster position="top-right" richColors />
      <div className="print-toolbar sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="exercise-print-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>
        <div className="text-sm text-slate-600">Exercise Plan · {p.name}</div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={shareWA} className="border-emerald-300 text-emerald-700" data-testid="exercise-wa-btn">
            <MessageCircle size={14} className="mr-1" /> WhatsApp
          </Button>
          <Button size="sm" onClick={() => window.print()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="exercise-print-btn">
            <Printer size={14} className="mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page" data-testid="exercise-print-page">
        <div className="p-8">
          <header className="flex justify-between items-start pb-4 border-b-2 border-slate-900">
            <div className="flex gap-4 items-start">
              <div className="w-16 h-20 flex-shrink-0">{LOGO_SVG}</div>
              <img src={doctorPhotoUrl()} alt={CLINIC.doctorName} className="h-16 w-16 rounded-full object-cover object-top border-2 border-pink-600 flex-shrink-0" data-testid="exercise-print-doctor-photo" />
              <div>
                <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{CLINIC.qualifications}</div>
                <div className="text-[10px] text-slate-500">{CLINIC.regNo}</div>
                <div className="text-[10px] font-semibold text-pink-700 mt-0.5">{CLINIC.specialties}</div>
              </div>
            </div>
            <div className="text-right text-[10px] text-slate-600 max-w-[220px] leading-snug">
              <div>{CLINIC.address}</div>
              {(CLINIC.phones || []).length > 0 && <div className="mt-1 tabular">{CLINIC.phones.join(" · ")}</div>}
            </div>
          </header>

          <section className="grid grid-cols-4 gap-2 text-[11px] mt-4 py-3 border-b border-slate-200">
            <div><span className="uppercase text-slate-500">Name:</span> <span className="font-medium">{p.name}</span></div>
            <div><span className="uppercase text-slate-500">Age/Sex:</span> {p.age} / {p.sex}</div>
            <div><span className="uppercase text-slate-500">Plan ID:</span> <span className="tabular">{plan.plan_id}</span></div>
            <div><span className="uppercase text-slate-500">Date:</span> <span className="tabular">{date}</span></div>
          </section>

          <section className="mt-4 py-3 border-b border-slate-200 text-center">
            <div className="uppercase text-[10px] tracking-[0.25em] text-indigo-600 font-semibold">{isMr ? "वैयक्तिक व्यायाम आराखडा" : "Personalised Exercise Protocol"}</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">{fit ? (isMr ? fit.name_mr : fit.name_en) : "Movement Plan"}</div>
          </section>

          <section className="mt-4 grid grid-cols-3 gap-2 text-[10.5px] text-slate-700">
            <div className="border border-slate-200 rounded px-2 py-1 bg-white"><div className="text-[8px] uppercase tracking-wider text-slate-500 font-semibold">BMI</div><div className="text-[12px] text-slate-800">{plan.preferences?.bmi || "—"}</div></div>
            <div className="border border-slate-200 rounded px-2 py-1 bg-white"><div className="text-[8px] uppercase tracking-wider text-slate-500 font-semibold">{isMr ? "क्षेत्र" : "Zone"}</div><div className="text-[12px] font-semibold text-indigo-800">{plan.preferences?.zone_en || "Moderate"}</div></div>
            <div className="border border-slate-200 rounded px-2 py-1 bg-white"><div className="text-[8px] uppercase tracking-wider text-slate-500 font-semibold">{isMr ? "मर्यादा" : "Constraints"}</div><div className="text-[11px] text-slate-800">{constraintsStr}</div></div>
          </section>

          <section className="mt-4 border border-slate-200 rounded p-4 bg-slate-50/40">
            <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-slate-800 font-sans">{plan.plan_text}</pre>
          </section>

          <section className="py-3 border-t border-slate-200 mt-6 flex justify-between items-end rx-signature-block">
            <div className="text-[11px] text-slate-500">
              <div className="uppercase text-[9px] tracking-wider text-slate-500 font-semibold">Plan No.</div>
              <div className="tabular font-medium text-slate-800">{plan.plan_id}</div>
            </div>
            <div className="text-right max-w-[62%]">
              <div className="border-t border-slate-800 pt-1 mt-8 text-[11px]">
                <div className="font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[9.5px] text-slate-600 leading-snug mt-0.5">{CLINIC.qualifications}</div>
              </div>
            </div>
          </section>

          <footer className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed print-doctor-footer">
            This exercise protocol is issued by <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong>, {CLINIC.name}. Movement is clinical medicine — stop immediately if you feel dizzy, breathless or develop chest pain, and contact the clinic.
          </footer>
        </div>

        <div className="print-marketing-footer border-t-2 border-indigo-600 bg-gradient-to-r from-indigo-50 via-white to-indigo-50 text-slate-700">
          <div className="grid grid-cols-3 gap-3 px-4 py-2">
            <div><div className="uppercase text-[9px] tracking-[0.25em] text-indigo-600 font-semibold">Movement As Medicine</div><div className="text-[10px] mt-0.5 leading-snug">GLUT4 activation and visceral fat reduction personalised to your profile.</div></div>
            <div><div className="uppercase text-[9px] tracking-[0.25em] text-indigo-600 font-semibold">Safety First</div><div className="text-[10px] mt-0.5 leading-snug">Carry glucose tablets, hydrate, and stop at the first warning sign.</div></div>
            <div><div className="uppercase text-[9px] tracking-[0.25em] text-indigo-600 font-semibold">Contact</div><div className="text-[10px] mt-0.5 leading-snug">{CLINIC.phones?.join(" · ")}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
